import { useEffect, useState } from 'react'

import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import type { ConsentConfigPayload } from '../consentConfig'

/**
 * Secondary consent copy from `GET /config/consents/secondary` (local default on the API).
 * This endpoint is independent of orders/orgs and never returns the primary applicability
 * errors (400 applicability codes), so secondary consents are always reachable.
 */
export type SecondaryConsentConfigError = 'upstream_error' | 'unavailable' | 'invalid_payload'

export type UseSecondaryConsentConfigResult = {
  payload: ConsentConfigPayload | null
  loading: boolean
  error: SecondaryConsentConfigError | null
}

export function useSecondaryConsentConfig(): UseSecondaryConsentConfigResult {
  const apiClient = useFamilyPortalApiClient()
  const [payload, setPayload] = useState<ConsentConfigPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<SecondaryConsentConfigError | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    apiClient
      .get('/config/consents/secondary')
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          setPayload(null)
          setError(res.status >= 500 ? 'unavailable' : 'upstream_error')
          return
        }
        try {
          const data = (await res.json()) as ConsentConfigPayload
          setPayload(data)
          setError(null)
        } catch {
          setPayload(null)
          setError('invalid_payload')
        }
      })
      .catch(() => {
        if (cancelled) return
        setPayload(null)
        setError('unavailable')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [apiClient])

  return { payload, loading, error }
}
